/**
 * v4 item 8: uncaught browser errors (`error`, `unhandledrejection`, the React error boundary) → POST /api/client-errors
 * (server log; Sentry only when the server has a DSN). At most 5 reports per page load and each message once; nothing
 * about the user: the page's path without its query string (?ref=, ?user=…), texts cut short, never a photo. A report
 * that fails is dropped, not retried nor reported.
 */
import type { ClientErrorReport } from "./api";

export const MAX_REPORTS = 5;
const MESSAGE_MAX = 500;
const STACK_MAX = 4000;
/** nothing to fix here: cross-origin scripts (ads, Google sign-in) give no detail; a harmless layout notice */
const IGNORED = [/^Script error\.?$/, /ResizeObserver loop/];

export type SendReport = (report: ClientErrorReport) => Promise<unknown>;
export type ReportError = (kind: ClientErrorReport["kind"], error: unknown, extraStack?: string) => void;

function describe(error: unknown): { message: string; stack: string } {
  if (error instanceof Error) return { message: `${error.name}: ${error.message}`, stack: error.stack ?? "" };
  if (typeof error === "string") return { message: error, stack: "" };
  try {
    return { message: JSON.stringify(error) ?? String(error), stack: "" };
  } catch {
    return { message: String(error), stack: "" };
  }
}

export function createErrorReporter(send: SendReport, path: () => string): ReportError {
  const seen = new Set<string>();
  let sent = 0;
  return (kind, error, extraStack = "") => {
    if (sent >= MAX_REPORTS) return;
    const { message, stack } = describe(error);
    if (IGNORED.some((re) => re.test(message))) return;
    const key = `${kind}:${message}`;
    if (seen.has(key)) return;
    seen.add(key);
    sent += 1;
    const report: ClientErrorReport = {
      kind,
      message: message.slice(0, MESSAGE_MAX),
      stack: [stack, extraStack].filter(Boolean).join("\n").slice(0, STACK_MAX),
      url: path(),
    };
    Promise.resolve().then(() => send(report)).catch(() => {
      /* offline, 429…: this report is dropped */
    });
  };
}

/** The page-wide handlers; the returned function also serves the React error boundary (main.tsx). */
export function installErrorReporting(send: SendReport, target: Window = window): ReportError {
  const report = createErrorReporter(send, () => target.location.pathname);
  target.addEventListener("error", (e: ErrorEvent) => report("error", e.error ?? e.message));
  target.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => report("unhandledrejection", e.reason));
  return report;
}
